/**
 * H5头部组件
 */
import React, { useEffect, useLayoutEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import classNames from 'classnames'
import { useLocation } from 'react-router-dom'
import { useScroll } from 'ahooks'
// import Env from '@/Api/domain'

import { isType, shareTo, sniff, backPage, shareConfigProps, updateAppShareDataConfig, setStatusBar, getStatusBarInfo } from '@/util'

import headerStyle from './Header.module.scss'

type Mode = 'dark' | 'light'

type Style = {
  // 组件包裹层的额外样式
  style?: React.CSSProperties
  // 左侧按钮的额外样式
  leftStyle?: React.CSSProperties
  // 右侧按钮的额外样式
  rightStyle?: React.CSSProperties
  // 标题栏的额外样式
}

type Left = React.ReactNode | undefined

type Right = React.ReactNode | undefined

type HeaderProps = {
  // 标题
  title?: string
  // 组件样式，现提供两种，light和dark
  mode?: Mode
  // 是否是页面header，页面header会兼容ios，iPhoneX等机型（native还未实现）
  pageHeader?: boolean
  // 组件包裹层的额外class
  extraClass?: string
  // 是否使用fixed定位
  isFixed: boolean
  // 使用fixed定位的话是否占位header
  isPlaceholder: boolean
  // 下拉是否展示白色头部
  isPullShowWhiteHeader: boolean
  // 是否沉浸式
  immersed: boolean
  // 左侧按钮，可传入多种类型
  left?: Left
  // 会作用到左边按钮
  onLeftTap?: () => void
  // 右侧按钮，可传入多种类型
  right?: Right
  // 自定义返回按钮
  backIcon?: React.ReactNode,
  // 自定义分享按钮
  shareIcon?: React.ReactNode,
  // 会作用到左边按钮
  onRightTap?: () => void
  // 滚动
  onScroll?: (scrollDoc: any) => void
  // 滚动时是否背景变白色
  scrollChange?: boolean
  // 标题的render函数(不传入则使用title)
  render?: React.ReactNode
  // 副header
  subHeader?: React.ReactNode
  shareConfigData?: shareConfigProps
  // 是否显示头部分享按钮
  showShareIcon?: boolean
  // 是否显示头部标题
  showTitleText?: boolean
  // 是否在组件中使用 禁用backPage 默认开启
  isNotBackPage: boolean
} & Style

const Header = (props: HeaderProps, ref: any) => {
  const { mode = 'light', immersed = false, isPullShowWhiteHeader = true, style, rightStyle, leftStyle, title, extraClass, isFixed = false, showShareIcon = true, showTitleText = true,  isPlaceholder = false, render, left, right, backIcon, shareIcon, subHeader, onRightTap, onLeftTap, shareConfigData, onScroll, scrollChange = true, isNotBackPage = false } = props
  const headerRef = useRef<HTMLDivElement>(null)
  const subHeaderRef = useRef<HTMLDivElement>(null)
  const timer = useRef<null| NodeJS.Timeout>(null)

  const [placeholderHeight, setPlaceholderHeight] = useState(0)
  const [statusBarHeight, setStatusBarHeight] = useState(0)
  const [ isScroll, setScrollStatus ] = useState(false)
  const location = useLocation()

  // 头部类名
  const headerClass = classNames(headerStyle.header,
    isFixed && `${headerStyle['fixed']}`,
    `${headerStyle[mode]}`,
    isScroll && `${headerStyle['scroll']}`,
    extraClass)

  // 监听页面滚动
  const scrollDoc = useScroll(document)
  useEffect(()=>{
    if( scrollDoc === undefined || ( immersed && !isPullShowWhiteHeader ) ) return
    const { top } = scrollDoc
    // 下拉头部高度则显示白色头
    if ( (top > placeholderHeight + 100) && scrollChange ) {
      setStatusBar('light')
      setScrollStatus(true)
    } else {
      setStatusBar(mode)
      setScrollStatus(false)
    }
    onScroll && onScroll(scrollDoc)
  }, [scrollDoc])

  // 沉浸式下状态栏高度
  useEffect(()=>{
    immersed && getStatusBarInfo().then((info)=>{
      setStatusBarHeight(info.height)
    })
  }, [immersed, location])

  useEffect(() => {
    shareConfigData && updateAppShareDataConfig(shareConfigData)
  }, [shareConfigData])

  useEffect(()=>{
    title && (document.title = title)
  }, [title])

  useLayoutEffect(() => {
    timer.current = setTimeout(() => {
      if (headerRef.current) {
        setPlaceholderHeight(headerRef?.current?.offsetHeight)
      }
    }, 100)

    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [subHeaderRef?.current?.offsetHeight, headerRef?.current?.offsetHeight, statusBarHeight])

  useImperativeHandle(ref, ()=> ({
    getHeaderHeight: () => {
      if (headerRef.current) {
        return headerRef.current.clientHeight
      }
    },
  }))

  const renderDefaultHeader = () => {

    if (!isType.isUndefined(render)) {
      return <div className={headerStyle.titleWrap}>
        {render}
      </div>
    }

    return <div className={headerStyle.titleWrap}>
      <span className={headerStyle.title}>{showTitleText==true ? title : ''}</span>
    </div>
  }

  const _backPage = () => {
    // if (window?.location?.hostname.indexOf('urboxwin.com') > -1 && window?.location?.hostname !== 'localhost') {
    //   if (window?.location?.hostname.indexOf('dev.urboxwin.com') > -1) {
    //     window.location.href = 'http://h5-dev.urboxwin.com/'
    //     // dev.urboxwin.com
    //   } else {
    //     window.location.href = 'https://www.urboxwin.com'
    //   }
    // } else {
    //   backPage()
    // }
    backPage()
  }

  const getBackIcon = () => {
    return backIcon ?
      <div className="iconSize"
          onClick={() => {
            if (!isNotBackPage) {
              // dev.urboxwin.com
              _backPage()
            }
        onLeftTap && onLeftTap()
      }}>
        {backIcon}
      </div> :
      <span className={headerStyle.leftIcon}
          onClick={() => {
            if (!isNotBackPage) {
              _backPage()
            }
          onLeftTap && onLeftTap()
        }}
          style={leftStyle}>
      </span>
  }

  // 头部左侧render
  const renderLeft = () => {
    return <div className={headerStyle.left}>
      {getBackIcon()}
      {
        !isType.isUndefined(left) && <> {left} </>
      }
    </div>
  }

  const getShareIcon = (shareConfigData: any) => {
    let shareBtn = null

    shareBtn = shareIcon ?
    <div className="iconSize"
        onClick={() => {
        shareTo(shareConfigData)
        onRightTap && onRightTap()
      }}>
      {shareIcon}
    </div> :
    <div className={mode === 'light' ? headerStyle.shareIcon : headerStyle.shareIconDark}
        onClick={() => {
        shareTo(shareConfigData)
        onRightTap && onRightTap()
      }}
        style={rightStyle}></div>

    // 在微信中右侧分享按钮不显示
    if ( sniff.isWechatMiniProgram ) {
      shareBtn = null
    }

    return shareBtn
  }
  // 头部右侧render
  const renderRight = () => {
    let res = null

    if (!isType.isUndefined(right)) {
      if (shareConfigData && sniff.isUboxApp) {
        res = <div className={headerStyle.right}>
          {right}
          {showShareIcon && getShareIcon(shareConfigData)}
        </div>
      } else {
        res = <div className={headerStyle.right}>
          {right}
        </div>
      }
    } else {
      if (shareConfigData && sniff.isUboxApp) {
        res = <div className={headerStyle.right}>
          {showShareIcon && getShareIcon(shareConfigData)}
        </div>
      } else {
        res = null
      }
    }
    // App内显示分享
    return res
  }

  // 副头部render
  const renderSubHeader = () => {
    if (!isType.isUndefined(subHeader)) {
      return <div
          id="subHeader"
          ref={subHeaderRef}>
        {subHeader}
      </div>
    }
  }

  return (
    <>
      {
        sniff.isWechat ? null : <>
          <header
              className={headerClass}
              ref={headerRef}
              style={{...style, ...{
                'paddingTop': statusBarHeight + 'px',
              }}}
          >
            <div className={headerStyle.headerInner}>
              {renderLeft()}
              {renderDefaultHeader()}
              {renderRight()}
              {renderSubHeader()}
            </div>
          </header >
          {
            isFixed && isPlaceholder && <div
                className={headerStyle.placeholder}
                style={{
                height: `${placeholderHeight}px`,
              }}
            ></div>
          }
        </>
      }
    </>
  )
}

export default forwardRef(Header)